import type {
	Business,
	List,
	UserNotes
} from '@/modules/dashboard/dashboardTypes';

import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import {
	CommunicationSession,
	type CommunicationSessionProps
} from './src/components/CommunicationSession';
import { AboutSession } from './src/components/AboutSession';
import {
	SummarySession,
	SummarySessionProps
} from './src/components/SummarySession';
import { StaffInfoSession } from './src/components/StaffInfoSession';
import {
	CategoriesSession,
	CategoriesSessionHandles,
	CategoriesSessionProps
} from './src/components/CategoriesSession';
import { OnlinePresenceSession } from './src/components/OnlinePresenceSession';
import { AddressSession } from './src/components/AddressSession';
import { WorkingHoursSession } from './src/components/WorkingHoursSession';

export type BusinessInfoTabProps = Pick<SummarySessionProps, 'appliedFilters'> &
	Pick<CategoriesSessionProps, 'categories'> &
	Pick<
		CommunicationSessionProps,
		| 'openAddDataModal'
		| 'onSetAsContacted'
		| 'openReportIncorrectDataModal'
		| 'openComposeEmail'
	> & {
		selectBusiness: Business;
		openSetReminder: () => void;
		selectedList?: List;
		saveNote?: (
			businessId: string,
			businessName: string,
			notes: UserNotes
		) => void;
		openDeleteDataModal: (x: number) => void;
		goToMap?: (businessId: string) => void;
		parentNodeName?: string;
	};

export type BusinessInfoTabHandles = {
	onHighlightCategories: VoidFunction;
};

const BusinessInfoTab = forwardRef<
	BusinessInfoTabHandles,
	BusinessInfoTabProps
>(
	(
		{
			appliedFilters,
			categories,
			selectBusiness,
			openAddDataModal,
			onSetAsContacted,
			openComposeEmail,
			saveNote,
			openDeleteDataModal,
			goToMap,
			openReportIncorrectDataModal
		},
		ref
	) => {
		const categoriesRef = useRef<CategoriesSessionHandles>(null);

		const handleHighlightCategories = useCallback(() => {
			categoriesRef.current?.onHighlightCategories();
		}, []);

		useImperativeHandle(ref, () => ({
			onHighlightCategories: handleHighlightCategories
		}));

		const handleSaveNote = (
			activeKey: keyof NonNullable<Business['contactType']>
		) => {
			if (!selectBusiness || !saveNote) return;

			saveNote(
				selectBusiness.businessId || (selectBusiness._id as string),
				selectBusiness.name,
				{
					contactType: {
						...selectBusiness.contactType,
						[activeKey]: true
					},
					isContacted: selectBusiness.isContacted,
					lead: selectBusiness?.lead,
					note: selectBusiness?.note ?? '',
					userAddedData: selectBusiness.userAddedData
				}
			);
		};

		if (!selectBusiness) return null;

		return (
			<div className="relative flex h-full w-full flex-col gap-6 p-6 pt-7">
				<AboutSession about={selectBusiness.yelp?.about} />

				<div className="flex w-full flex-col items-start gap-2">
					<strong className="flex items-center font-semibold text-dm-mid-grey">
						Business Information
					</strong>

					<section className="flex w-full flex-col gap-5 rounded-lg border border-dm-grey px-2 py-3">
						<SummarySession
							yearIncorporated={selectBusiness.yearIncorporated}
							yearInBusiness={selectBusiness.yearInBusiness}
							customData={selectBusiness.customData}
							appliedFilters={appliedFilters}
						/>

						<StaffInfoSession
							contactNames={selectBusiness.contactNames}
							businessBureau={selectBusiness.businessBureau}
						/>

						<CategoriesSession
							categories={categories}
							ref={categoriesRef}
						/>
					</section>
				</div>

				<CommunicationSession
					_id={selectBusiness._id}
					email={selectBusiness.email}
					phone={selectBusiness.phone}
					facebook={selectBusiness.facebook}
					web={selectBusiness.web}
					userAddedData={selectBusiness.userAddedData}
					emailStatus={selectBusiness.emailStatus}
					socials={selectBusiness.socials}
					contactType={selectBusiness.contactType}
					openAddDataModal={openAddDataModal}
					onSetAsContacted={onSetAsContacted}
					openReportIncorrectDataModal={openReportIncorrectDataModal}
					openComposeEmail={openComposeEmail}
				/>

				<OnlinePresenceSession
					_id={selectBusiness._id}
					name={selectBusiness.name}
					website={selectBusiness.website}
					address={selectBusiness.address}
					facebook={selectBusiness.facebook}
					socials={selectBusiness.socials}
					yelp={selectBusiness.yelp}
					openDeleteDataModal={openDeleteDataModal}
					onSaveNote={handleSaveNote}
				/>

				<AddressSession
					_id={selectBusiness._id}
					address={selectBusiness.address}
					goToMap={goToMap}
				/>

				<WorkingHoursSession hours={selectBusiness.hours} />
			</div>
		);
	}
);

export default BusinessInfoTab;
